import React, { useEffect } from 'react';
import useAnalytics from 'helpers/useAnalytics';
import _get from 'lodash/get';
import makeStyles from '@material-ui/core/styles/makeStyles';

import Typography from '@material-ui/core/Typography';
import ErrorRoundedIcon from '@material-ui/icons/ErrorRounded';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';

import { MappingValidationResult } from 'types/Mapping';
import { Theme } from 'styles/ThemeTyping';

interface Props {
  errors: MappingValidationResult[];
  warnings: MappingValidationResult[];
}

function CsvUploadIssues({ errors, warnings }: Props) {
  const classes = useStyles();
  const analytics = useAnalytics();
  const headerText = errors.length > 0 ? 'Issues Found' : 'Before You Continue';
  const subheadingText =
    errors.length > 0
      ? `We’ve found some issues with your file that require your attention. See below for
            instructions on how to update your file, then upload a new one when you’re ready.`
      : `We wanted to make sure you’ve included everything you need in your file. Read through the warnings below, and upload a new file if there’s any of them that you’d like to address.`;

  useEffect(() => {
    if (errors.length > 0) {
      analytics.track('Data Sync - “Issues Found” Dialog Appear', {
        category: 'AD - Directories',
      });
    }

    if (!errors.length && warnings.length > 0) {
      analytics.track('Data Sync - “Before You Continue” Dialog Appear', {
        category: 'AD - Directories',
      });
    }
  }, [errors, warnings]);

  return (
    <div className={classes.root}>
      <Typography variant="h5">{headerText}</Typography>
      <Typography variant="body2" className={classes.subheading}>
        {subheadingText}
      </Typography>

      {errors.length > 0 && (
        <>
          <Typography className={classes.issuesHeader}>
            Errors Found - Must Be Fixed Before Continuing
          </Typography>

          <div className={classes.errorsContainer}>
            {errors.map((error: MappingValidationResult, index: number) => {
              return (
                <div className={classes.error} key={index}>
                  <ErrorRoundedIcon className={classes.errorIcon} />
                  <div dangerouslySetInnerHTML={{ __html: error.longDescriptionHtml }} />
                </div>
              );
            })}
          </div>
        </>
      )}

      {warnings.length > 0 && (
        <>
          <Typography className={classes.issuesHeader}>
            Warnings - Optional but Recommended Fixes
          </Typography>

          <div className={classes.warningsContainer}>
            {warnings.map((warning: MappingValidationResult, index: number) => {
              return (
                <div className={classes.warning} key={index}>
                  <WarningRoundedIcon className={classes.warningIcon} />
                  <div dangerouslySetInnerHTML={{ __html: warning.longDescriptionHtml }} />
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '100%',
  },
  subheading: {
    color: theme.colors.gray1,
  },
  issuesHeader: {
    fontWeight: theme.typography.h4.fontWeight,
    marginBottom: theme.spacing(),
    marginTop: theme.spacing(4),
  },
  errorsContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  error: {
    display: 'flex',
    marginBottom: theme.spacing(),
  },
  errorIcon: {
    marginRight: theme.spacing(),
    marginTop: theme.spacing(1.5),
    color: theme.palette.error.main,
  },
  warningsContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  warning: {
    display: 'flex',
    marginBottom: theme.spacing(),
  },
  warningIcon: {
    marginRight: theme.spacing(),
    marginTop: theme.spacing(1.5),
    color: theme.palette.warning.main,
  },
}));

export default CsvUploadIssues;
